// @flow

import { hpfetch } from '../util';
import type { purchaseType, addonType } from '../../types';
import type {
  djangoModelApiResponse,
  wrappedApiResponseType,
} from '../apiTypes';

export const getSingle = (
  id: number,
): Promise<wrappedApiResponseType<purchaseType>> => hpfetch(`purchases/${id}/`);

export const getMany = (
  limit: number = 100,
  offset: number = 0,
): djangoModelApiResponse<purchaseType> =>
  hpfetch(`purchases/?limit=${limit}&offset=${offset}`);

export const estimatePurchase = (
  id: number,
  pricingStructures: Array<string>,
  estimateFields: {},
  location: any,
  offering_pricing_fields: {} | void,
  offering_escrow_fields: {} | void,
): Promise<wrappedApiResponseType<purchaseType>> =>
  hpfetch(`offerings/${id}/estimate/`, {
    method: 'POST',
    body: JSON.stringify({
      estimate_fields: estimateFields,
      pricing_structures: pricingStructures,
      ...(location ? { location } : {}),
      offering_pricing_fields,
      offering_escrow_fields,
    }),
  });

export const estimateEscrowPurchase = (
  id: number,
  pricing_structures: Array<string>,
  escrow_percentage: number,
  selected_dates: Array<any>,
  escrow_fields: {},
  lands: Array<number>,
  location: any,
  coupon: string,
  offering_pricing_fields: {} | void,
  offering_escrow_fields: {} | void,
  addons: Array<addonType> | void,
  use_balance: boolean,
  use_credits: boolean,
  payment_method: number | void | null,
): Promise<wrappedApiResponseType<purchaseType>> => {
  const body: any = {
    pricing_structures,
    escrow_percentage,
    available_dates: selected_dates,
    escrow_fields,
    land_ids: lands,
    offering_pricing_fields,
    offering_escrow_fields,
    buyer_offer_addons: addons,
    use_balance,
    use_credits,
    payment_method,
  };

  if (location) {
    body.location = location;
  }

  if (coupon) {
    body.coupon_codes = [coupon];
  }

  return hpfetch(`offerings/${id}/estimate_escrow/`, {
    method: 'POST',
    body: JSON.stringify(body),
  });
};

export const doPurchase = (
  id: number,
  pricing_structures: Array<string>,
  selected_dates: Array<any>,
  escrow_fields: {},
  escrow_percentage: number,
  payment_method: number,
  lands: Array<number>,
  location: any,
  coupon: string,
  offering_pricing_fields: {} | void,
  offering_escrow_fields: {} | void,
  addons: Array<addonType> | void,
  buyer_waiver_ids: number[],
  has_agreements: boolean,
  buyer_agree: boolean,
  buyer_attachment_ids: number[],
  use_credits: boolean,
  use_balance: boolean,
): Promise<wrappedApiResponseType<purchaseType>> => {
  const body: any = {
    offering_id: id,
    pricing_structures,
    available_dates: selected_dates,
    escrow_fields,
    escrow_percentage,
    payment_method,
    land_ids: lands,
    offering_pricing_fields,
    offering_escrow_fields,
    buyer_offer_addons: addons,
    buyer_waiver_ids,
    has_agreements,
    buyer_agree,
    buyer_attachment_ids,
    use_credits,
    use_balance,
  };

  if (location) {
    body.location = location;
  }

  if (coupon) {
    body.coupon_codes = [coupon];
  }

  return hpfetch('purchases/', {
    method: 'POST',
    body: JSON.stringify(body),
  });
};

const updatePurchaseStatus = (status: string) => (id: number) =>
  hpfetch(`purchases/${id}/`, {
    method: 'PATCH',
    body: JSON.stringify({
      status,
    }),
  });

export const reportPurchase = (id: number) =>
  updatePurchaseStatus('reported')(id);
export const toBuyerCanceled = (id: number) =>
  updatePurchaseStatus('buyer_canceled')(id);
export const toPaid = (id: number) => updatePurchaseStatus('paid')(id);
export const toAwaitingSecondPaymentSellerApproval = (id: number) =>
  updatePurchaseStatus('awaiting_second_payment_seller_approval')(id);

export const updateBuyerPricingFieldsWithAddons = (
  id: number,
  fields: any,
  addons: any,
) =>
  hpfetch(`purchases/${id}/`, {
    method: 'PATCH',
    body: JSON.stringify({
      seller_pricing_fields: fields || {},
      final_addons: addons || [],
    }),
  });

export const toComplete = (id: number) => updatePurchaseStatus('complete')(id);
export const toCompleteWithPaymentMethod = (
  id: number,
  paymentMethod: number,
) =>
  hpfetch(`purchases/${id}/`, {
    method: 'PATCH',
    body: JSON.stringify({
      status: 'complete',
      payment_method: paymentMethod,
    }),
  });

export const report = (id: number, comment: string) =>
  hpfetch(`purchases/${id}/`, {
    method: 'PATCH',
    body: JSON.stringify({
      reported: true,
      comment,
    }),
  });

export const updateBuyerMiscAdjustment = (
  id: number,
  seller_adjustment: number,
  seller_adjustment_reason: string,
) =>
  hpfetch(`purchases/${id}/`, {
    method: 'PATCH',
    body: JSON.stringify({
      seller_adjustment,
      seller_adjustment_reason,
    }),
  });

export const updateBuyerFinalPricingAdjustment = (
  id: number,
  final_pricing_fields: {},
) =>
  hpfetch(`purchases/${id}/`, {
    method: 'PATCH',
    body: JSON.stringify({
      final_pricing_fields,
    }),
  });

export const updateBuyerFinalPricingWithAddonsAdjustment = (
  id: number,
  final_pricing_fields: {},
  addons: any,
) =>
  hpfetch(`purchases/${id}/`, {
    method: 'PATCH',
    body: JSON.stringify({
      final_pricing_fields,
      final_addons: addons || [],
    }),
  });

export const addAttachment = (id: number, buyer_attachment_ids: number[]) =>
  hpfetch(`purchases/${id}/`, {
    method: 'PATCH',
    body: JSON.stringify({
      buyer_attachment_ids,
    }),
  });

// export const addAgreements = (id: number, agreement: agreementType) =>
//   hpfetch(`purchases/${id}/agreements/`, {
//     method: 'POST',
//     // body: JSON.stringify(agreement),
//     body: agreement
//   });
